import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MapPin, Globe, ArrowLeft, Calendar, BookOpen, UserPlus, MessageCircle, Check, Clock } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { splitSpecialties } from '../utils/specialty';
import { READINESS_CATEGORIES, getReadinessCategory } from '../utils/readiness';
import { resolveImageUrl } from '../utils/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PillarProfilePage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user: me, isAuthenticated } = useAuth();
    const [pillar, setPillar] = useState(null);
    const [events, setEvents] = useState([]);
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    // Connection state with this pillar: null | 'none' | 'pending_out' | 'pending_in' | 'accepted'
    const [connState, setConnState] = useState(null);
    const [connId, setConnId] = useState(null);
    const [sendingRequest, setSendingRequest] = useState(false);
    const [showRequestModal, setShowRequestModal] = useState(false);
    const [requestMessage, setRequestMessage] = useState('');

    useEffect(() => {
        (async () => {
            try {
                // Load the pillar and their public output (events + library items)
                const [{ data: p }, { data: evts }, { data: libs }] = await Promise.all([
                    axios.get(`${API_URL}/api/pillars/${id}`),
                    axios.get(`${API_URL}/api/events`),
                    axios.get(`${API_URL}/api/library`)
                ]);
                setPillar(p);
                setEvents((evts || []).filter(e => e.creator_id === id));
                setResources((libs || []).filter(r => r.creator_id === id));
            } catch (err) {
                setNotFound(true);
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    // Look up whether I'm already connected / have a pending request with this pillar
    useEffect(() => {
        if (!isAuthenticated || !id || !me?.id || String(me.id) === String(id)) {
            setConnState(null);
            return;
        }
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/connections`, { withCredentials: true });
                const match = (data || []).find(c => String(c.other_user_id) === String(id));
                if (!match) {
                    setConnState('none');
                    setConnId(null);
                    return;
                }
                setConnId(match.id);
                if (match.status === 'accepted') setConnState('accepted');
                else if (match.status === 'pending' && String(match.from_user_id) === String(me.id)) setConnState('pending_out');
                else if (match.status === 'pending') setConnState('pending_in');
                else setConnState('none');
            } catch (err) {
                setConnState('none');
            }
        })();
    }, [id, isAuthenticated, me?.id]);

    const sendRequest = async () => {
        setSendingRequest(true);
        try {
            await axios.post(
                `${API_URL}/api/connections/request`,
                { to_user_id: id, message: requestMessage || undefined },
                { withCredentials: true }
            );
            toast.success('Connection request sent');
            setShowRequestModal(false);
            setRequestMessage('');
            setConnState('pending_out');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not send request');
        } finally {
            setSendingRequest(false);
        }
    };

    const openChat = () => {
        if (connId) navigate(`/connections?open=${connId}`);
        else navigate('/connections');
    };

    const isSelf = me?.id && String(me.id) === String(id);

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <Navbar />
            <main className="pt-24 pb-16" data-testid="pillar-profile-page">
                <div className="max-w-5xl mx-auto px-6 sm:px-12">
                    <Link
                        to="/pillars"
                        className="inline-flex items-center gap-2 text-sm text-[#5B2D8E] hover:underline mb-8"
                        data-testid="back-to-pillars"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to Pillars
                    </Link>

                    {loading ? (
                        <div className="text-center py-16">
                            <div className="w-12 h-12 border-4 border-[#5B2D8E] border-t-transparent rounded-full animate-spin mx-auto"></div>
                        </div>
                    ) : notFound || !pillar ? (
                        <div className="text-center py-16 bg-white border border-[#E5E0D8] rounded-sm">
                            <p className="text-[#4A4A68]">Pillar not found.</p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-16">
                                <div>
                                    <div className="aspect-square bg-gradient-to-br from-[#5B2D8E]/10 to-[#D8B4E2]/20 rounded-sm overflow-hidden flex items-center justify-center">
                                        {pillar.profile_image_url ? (
                                            <img src={resolveImageUrl(pillar.profile_image_url)} alt={pillar.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="font-serif text-7xl text-[#5B2D8E]">{(pillar.name || '?').charAt(0)}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="md:col-span-2">
                                    <p className="overline mb-3">Mirror Circle Partner</p>
                                    <div className="flex flex-wrap items-center gap-3 mb-3">
                                        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A2E]" data-testid="pillar-name">
                                            {pillar.name}
                                        </h1>
                                        {pillar.is_founder && (
                                            <span
                                                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-2.5 py-1 bg-[#5B2D8E] text-white rounded-sm"
                                                data-testid="pillar-founder-badge"
                                            >
                                                Founder
                                            </span>
                                        )}
                                    </div>
                                    {splitSpecialties(pillar.specialty).length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="pillar-specialties">
                                            {splitSpecialties(pillar.specialty).map(tag => (
                                                <span
                                                    key={tag}
                                                    className="text-[11px] uppercase tracking-widest px-2.5 py-1 border border-[#5B2D8E]/30 text-[#5B2D8E] rounded-sm"
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    {Array.isArray(pillar.readiness_categories) && pillar.readiness_categories.length > 0 && (
                                        <div className="mb-6 space-y-2" data-testid="pillar-readiness">
                                            <p className="text-[11px] uppercase tracking-widest text-[#4A4A68]">Audience readiness</p>
                                            {pillar.readiness_categories.map(key => {
                                                const cat = getReadinessCategory(key);
                                                if (!cat) return null;
                                                return (
                                                    <div
                                                        key={key}
                                                        className={`flex items-start gap-3 p-3 rounded-sm border ${cat.badgeClass}`}
                                                    >
                                                        <span className={`shrink-0 inline-block w-2 h-2 rounded-full mt-1.5 ${cat.dotClass}`} />
                                                        <div className="flex-1">
                                                            <p className="text-sm font-medium">
                                                                {cat.key} — {cat.title}
                                                            </p>
                                                            <p className="text-xs mt-0.5 opacity-80">{cat.subtitle}</p>
                                                            <p className="text-xs mt-1 opacity-80">{cat.description}</p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <div className="flex flex-wrap gap-4 text-sm text-[#4A4A68] mb-6">
                                        {(pillar.city || pillar.country) && (
                                            <span className="flex items-center gap-2">
                                                <MapPin className="w-4 h-4" /> {[pillar.city, pillar.country].filter(Boolean).join(', ')}
                                            </span>
                                        )}
                                        {pillar.website && (
                                            <a href={pillar.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#5B2D8E] hover:underline">
                                                <Globe className="w-4 h-4" /> Website
                                            </a>
                                        )}
                                    </div>

                                    {/* Connection CTA — anchored under the metadata so it always
                                        shows exactly ONE action: connect, pending, accept, or message. */}
                                    {!isSelf && (
                                        <div className="mb-6" data-testid="pillar-connect-section">
                                            {!isAuthenticated ? (
                                                <Link
                                                    to={`/login?next=${encodeURIComponent(`/pillars/${id}`)}`}
                                                    className="inline-flex items-center gap-2 btn-primary text-sm px-5 py-2.5"
                                                    data-testid="pillar-signin-to-connect"
                                                >
                                                    <UserPlus className="w-4 h-4" /> Sign in to connect
                                                </Link>
                                            ) : connState === 'accepted' ? (
                                                <button
                                                    onClick={openChat}
                                                    className="inline-flex items-center gap-2 btn-primary text-sm px-5 py-2.5"
                                                    data-testid="pillar-open-chat"
                                                >
                                                    <MessageCircle className="w-4 h-4" /> Message {pillar.name.split(' ')[0]}
                                                </button>
                                            ) : connState === 'pending_out' ? (
                                                <span
                                                    className="inline-flex items-center gap-2 text-sm px-5 py-2.5 border border-[#5B2D8E]/40 text-[#5B2D8E] rounded-sm"
                                                    data-testid="pillar-pending-out"
                                                >
                                                    <Clock className="w-4 h-4" /> Request sent — awaiting reply
                                                </span>
                                            ) : connState === 'pending_in' ? (
                                                <button
                                                    onClick={openChat}
                                                    className="inline-flex items-center gap-2 btn-primary text-sm px-5 py-2.5"
                                                    data-testid="pillar-pending-in"
                                                >
                                                    <Check className="w-4 h-4" /> They requested to connect — open to accept
                                                </button>
                                            ) : connState === 'none' ? (
                                                <button
                                                    onClick={() => setShowRequestModal(true)}
                                                    className="inline-flex items-center gap-2 btn-primary text-sm px-5 py-2.5"
                                                    data-testid="pillar-connect-btn"
                                                >
                                                    <UserPlus className="w-4 h-4" /> Connect with {pillar.name.split(' ')[0]}
                                                </button>
                                            ) : null}
                                        </div>
                                    )}

                                    {pillar.bio && (
                                        <div className="prose max-w-none text-[#1A1A2E] whitespace-pre-wrap">
                                            {pillar.bio}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {events.length > 0 && (
                                <section className="mb-16">
                                    <h2 className="font-serif text-2xl text-[#1A1A2E] mb-6 flex items-center gap-3">
                                        <Calendar className="w-6 h-6 text-[#5B2D8E]" /> Events by {pillar.name}
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="pillar-events">
                                        {events.map(e => (
                                            <Link
                                                key={e.id}
                                                to="/events"
                                                className="block p-6 bg-white border border-[#E5E0D8] rounded-sm hover:border-[#5B2D8E] transition-colors"
                                            >
                                                <p className="text-xs uppercase tracking-widest text-[#5B2D8E] mb-2">
                                                    {new Date(e.date).toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' })}
                                                </p>
                                                <h3 className="font-serif text-xl text-[#1A1A2E] mb-2">{e.title}</h3>
                                                <p className="text-sm text-[#4A4A68] line-clamp-2">{e.description}</p>
                                            </Link>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {resources.length > 0 && (
                                <section>
                                    <h2 className="font-serif text-2xl text-[#1A1A2E] mb-6 flex items-center gap-3">
                                        <BookOpen className="w-6 h-6 text-[#5B2D8E]" /> Library from {pillar.name}
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="pillar-library">
                                        {resources.map(r => (
                                            <Link
                                                key={r.id}
                                                to="/library"
                                                className="block p-6 bg-white border border-[#E5E0D8] rounded-sm hover:border-[#5B2D8E] transition-colors"
                                            >
                                                <p className="text-xs uppercase tracking-widest text-[#5B2D8E] mb-2">
                                                    {r.category} · {r.content_type}
                                                </p>
                                                <h3 className="font-serif text-xl text-[#1A1A2E] mb-2">{r.title}</h3>
                                                <p className="text-sm text-[#4A4A68] line-clamp-2">{r.description}</p>
                                            </Link>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </>
                    )}
                </div>
            </main>

            {/* Connection request modal — mirrors the Connections page prompt */}
            {showRequestModal && pillar && (
                <div
                    className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
                    onClick={() => !sendingRequest && setShowRequestModal(false)}
                    data-testid="pillar-request-modal"
                >
                    <div
                        className="bg-white rounded-sm max-w-md w-full p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="font-serif text-2xl text-[#1A1A2E] mb-2">
                            Reach out to {pillar.name.split(' ')[0]}
                        </h3>
                        <p className="text-sm text-[#4A4A68] mb-4">
                            Add a short message (optional) so they know what sparked your interest.
                        </p>
                        <textarea
                            value={requestMessage}
                            onChange={(e) => setRequestMessage(e.target.value)}
                            maxLength={500}
                            rows={4}
                            placeholder={`Hi ${pillar.name.split(' ')[0]}, I'd love to connect because…`}
                            className="w-full px-3 py-2 border border-[#E5E0D8] rounded-sm focus:border-[#5B2D8E] focus:outline-none text-sm"
                            data-testid="pillar-request-message"
                        />
                        <div className="flex gap-2 justify-end mt-4">
                            <button
                                onClick={() => setShowRequestModal(false)}
                                disabled={sendingRequest}
                                className="btn-secondary text-sm px-4 py-2"
                                data-testid="pillar-request-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={sendRequest}
                                disabled={sendingRequest}
                                className="btn-primary text-sm px-4 py-2 flex items-center gap-2"
                                data-testid="pillar-request-send"
                            >
                                {sendingRequest ? 'Sending…' : (<><UserPlus className="w-4 h-4" /> Send request</>)}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}
